import { Injectable, OnDestroy, inject } from '@angular/core';
import ShortUniqueId from 'short-unique-id';
import { HttpClient } from '@angular/common/http';

// firestore functions
// DocumentData, addDoc, updateDoc, deleteDoc, DocumentReference,
import {
  Firestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  or,
  addDoc,
  getDocs,
} from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { BuyContract, SellContract } from '../interfaces/orders-interface';
import { IItem, UtilitiesModule } from '@coreui/angular-pro';
import { SystemService } from './system.service';
import { DualContractSyncService } from './dual-contract-sync.service';
import { environment } from '../../environments/environment';
import { getTextOfJSDocComment } from 'typescript';
import { log } from 'console';
// export interface ContractAbstract  {
//   contractID: string;
//   commodityCode: string;
//   contractQuantity: number;
//   contractPrice: number;
//   status : string;
//   deliveryDate: Date;
// }

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  private firestore: Firestore = inject(Firestore); // inject Cloud Firestore
  private functions = inject(Functions); // inject Cloud Functions
  private systemServices = inject(SystemService);
  private dualContractSync = inject(DualContractSyncService); // inject Dual Contract Sync Service
  private http = inject(HttpClient); // inject HTTP client for API calls
  private currentBusinessID: string = '';
  private isWatchingContracts: boolean = false;
  private apiUrl = environment.apiUrl; // LAK-333: Backend API endpoint

  myBusinessID: string = '';
  buyContracts: BuyContract[] = [];
  buyContractsWatcher: any;
  buyContractsObserver: any;
  sellContracts: SellContract[] = [];
  sellContractsWatcher: any;
  sellContractsObserver: any;
  allContracts: any = {
    new: [],
    pending: [],
    fulfill: [],
    invoice: [],
    dispatch: [],
    closed: [],
  };
  abstracts: any[] = [];
  transactionLogs: any = {};
  transactionLogsSubject = new BehaviorSubject<any>({});
  transactionLogsWatcher: any = undefined;
  isWatchingTransactionLogs: boolean = false;
  showContracts: boolean = true;
  tallyOfContracts: any = {
    numBuyContracts: 0,
    numSellContracts: 0,
    numPending: 0, // pending
    numToBook: 0, // pending: for booking
    numBooked: 0, // pending: booked for delivery
    numToFund: 0, // pending: for pre-funding
    numFunded: 0, // pending: for pre-funded
    numFulfillment: 0, // delivery :  num for fulfillment
    numInbound: 0, // delivery : num for inbound
    numReceiving: 0, // delivery : received -
    numInvoicing: 0, // invoicing: total for payment
    numToTransfer: 0, // invoicing: to transfer payment (no transfer-payment log)
    numConfirmedTransfer: 0, // invoicing: confirmed transfer (has transfer-payment log, but no transfer-confirmed log)
    numTransferred: 0, // invoicing: transferred (has transfer-confirmed log)
    numToIssueInvoice: 0, // invoicing: for invoice issuance (no invoice-issued log)
    numInvoiceIssued: 0, // invoicing: invoice issued (has invoice-issued log)
    numPacked: 0, // dispatch: packed for delivery
    numInTransit: 0, // dispatch: in transit to buyer
    numDispatch: 0, // dispatch: total number in dispatch
    numReleased: 0, // dispatch: released to buyer
    numClosed: 0, // closed: contract fully received
    numToDisburse: 0, // disbursement: contracts ready for seller to withdraw funds
    numDisbursed: 0, // disbursement: contracts with funds already disbursed
  };
  abstractsSubject = new BehaviorSubject<any[]>([]);
  tallyOfContractsSubject = new BehaviorSubject<any>(this.tallyOfContracts);


  /** Contract statii
   * # - queue - status - description
   * 0 - open - open - for acceptance, not yet a fully signed contract
   * 1a - pending - booking - contracted, but delivery date is still far away
   * 1b - pending - funding - buyer has to pre-fund the contract prior to delivery of goods
   * 2 - fulfill - receiving - order has been received by stockpoint
   * 3 - invoice - payment - transfer of payment to seller
   * 4 - dispatch - dispatch - lot is ready to be shipped to buyer
   * 5 - closed - fulfilled - lot has been shipped to buyer
   *
   *      status     |  buyer action              | seller action
   * 1 - pending     |     pre-fund               | book delivery
   * 2 - fulfill     |  view incoming             | for delivery
   * 3 - invoice     |  view payment / WHS Receipt| issue sales invoice
   * 4 - dispatch    |  view claim stub           |
   * 5 - closed      |  <archive - view contract> | <archived>
   */

  constructor() {}

  /**
   * blankBuyContract creates a blankBuyContract
   * useful when creating the variable but not yet defining it
   * this is a utility function for ESLint compliance
   * @returns a blank Buy contract
   */

  blankBuyContract(): BuyContract {
    var blank: BuyContract = {
      contractID: '',
      orderID: '',
      buyerID: '',
      buyerBusinessID: '',
      sellerID: '',
      sellerBusinessID: '',
      commodity: this.systemServices.blankCommodity(),
      contractQuantity: 0,
      contractPrice: 0,
      contractType: 'buy',
      deliveryDate: new Date(),
      stockpoint: this.systemServices.blankStockpoint(),
      testingSpecs: [],
      acceptanceSpecs: [],
      paymentTerms: '',
      sellerRequests: [],
      sellerCharges: [],
      buyerRequests: [],
      buyerCharges: [],
      lakalCharges: [],
      status: 'blank',
      routing: null,
    };
    return blank;
  }

  blankSellContract(): SellContract {
    var blank: SellContract = {
      contractID: '',
      orderID: '',
      buyerID: '',
      buyerBusinessID: '',
      sellerID: '',
      sellerBusinessID: '',
      commodity: this.systemServices.blankCommodity(),
      contractQuantity: 0,
      contractPrice: 0,
      contractType: 'sell',
      deliveryDate: new Date(),
      stockpoint: this.systemServices.blankStockpoint(),
      testingSpecs: [],
      acceptanceSpecs: [],
      paymentTerms: '',
      sellerRequests: [],
      sellerCharges: [],
      buyerRequests: [],
      buyerCharges: [],
      lakalCharges: [],
      status: 'blank',
      routing: null,
    };
    return blank;
  }

  async watchAllMyBuyContracts(
    myBusinessID: string
  ): Promise<Observable<any[]>> {
    if (this.buyContractsWatcher && this.currentBusinessID === myBusinessID && this.isWatchingContracts) {
      console.log('ContractsService: Reusing existing buy contracts subscription');
      return this.buyContractsObserver;
    }

    // Clean up if switching business
    if (this.currentBusinessID !== myBusinessID) {
      this.unwatchAllMyBuyContracts();
      this.unwatchTransactionLogs();
    }

    this.currentBusinessID = myBusinessID;
    this.isWatchingContracts = true;
    const allBuyContracts = new Subject<any[]>();
    console.log('watchAllMyBuyContracts', myBusinessID);
    console.log('🔍 [CONTRACTS-DEBUG] Setting up query for businessID:', myBusinessID);
    this.myBusinessID = myBusinessID;
    // Call backend tally API now that we have business ID
    this.getContractTallyFromBackend();
    if (this.buyContractsWatcher !== undefined && typeof this.buyContractsWatcher === 'function') {
      // can either just cancel the contracts watcher and create a new one
      console.log(this.buyContractsWatcher);
      this.buyContractsWatcher();
    }
    const buyContractsRef = collection(this.firestore, 'buyContracts');
    const q = query(
      buyContractsRef,
      or(
        where('buyerBusinessID', '==', myBusinessID),
        where('sellerBusinessID', '==', myBusinessID)
      )
    );
    const buyContractsSubscription = onSnapshot(q, (buyContracts) => {
      try {
        console.log('🔍 [CONTRACTS-DEBUG] Got buyContracts snapshot with', buyContracts.docs.length, 'contracts');
        console.log('🔍 [CONTRACTS-DEBUG] Business ID filter:', myBusinessID);

        // Log raw documents before processing
        buyContracts.docs.forEach(doc => {
          const rawData = doc.data();
          if (rawData['contractID'] === 'BFORXO-6SPX84') {
            console.log('🔍 [CONTRACTS-DEBUG] RAW SNAPSHOT BFORXO-6SPX84:', {
              documentID: doc.id,
              contractID: rawData['contractID'],
              status: rawData['status'],
              buyerBusinessID: rawData['buyerBusinessID'],
              sellerBusinessID: rawData['sellerBusinessID'],
              remainingAmount: rawData['remainingAmount'],
              paidAmount: rawData['paidAmount'],
              partialPayments: rawData['partialPayments'],
              hasPartialPaymentFields: !!(rawData['remainingAmount'] || rawData['paidAmount'] || rawData['partialPayments'])
            });
          }
        });

        const data = buyContracts.docs.map((doc) => {
          const contractData = Object.assign({}, doc.data(), {
            contractDocumentID: doc.id,
            deliveryDate: doc.data()['deliveryDate'] && typeof doc.data()['deliveryDate'].toDate === 'function'
              ? doc.data()['deliveryDate'].toDate()
              : null,
          });
          // Debug for BFORXO-6SPX84 to track partial payments
          if (contractData['contractID'] === 'BFORXO-6SPX84') {
            console.log(`🔍 [CONTRACTS-DEBUG] BuyContract BFORXO-6SPX84:`, {
              contractID: contractData['contractID'],
              status: contractData['status'],
              buyerBusinessID: contractData['buyerBusinessID'],
              sellerBusinessID: contractData['sellerBusinessID'],
              remainingAmount: contractData['remainingAmount'],
              paidAmount: contractData['paidAmount'],
              partialPayments: contractData['partialPayments']
            });
          }
          return contractData;
        });
        allBuyContracts.next(data);
        this.buyContracts = data as BuyContract[];
        this.sortBuyContracts();
        // sort first before doing the tally
        // get all the transactionLogs then perform the tally
        // this will also call tallyMyContracts
        this.tallyMyContractsLevel1();
        this.watchContractTransactionLogs();
      } catch(error) {
        console.error('ContractsService: Error processing buy contracts:', error);
        allBuyContracts.error(error);
      }
    }, (error) => {
      console.error('ContractsService: Buy contracts subscription error:', error);
      allBuyContracts.error(error);
    });
    this.buyContractsWatcher = buyContractsSubscription;
    this.buyContractsObserver = allBuyContracts.asObservable();
    return this.buyContractsObserver;
  }

  unwatchAllMyBuyContracts() {
    if (this.buyContractsWatcher && typeof this.buyContractsWatcher === 'function') {
      this.buyContractsWatcher();
      this.buyContractsWatcher = null;
    }
    this.isWatchingContracts = false;
    this.buyContracts = [];
  }

  async watchAllMySellContracts(
    myBusinessID: string
  ): Promise<Observable<any[]>> {
    const allSellContracts = new Subject<any[]>();
    this.myBusinessID = myBusinessID;
    // Call backend tally API now that we have business ID
    this.getContractTallyFromBackend();
    if (this.sellContractsWatcher !== undefined && typeof this.sellContractsWatcher === 'function') {
      // can either unsubscribe and create a new one or just send back the sellContractsObserver
      console.log('SellContractsWatcher', this.sellContractsWatcher);
      this.sellContractsWatcher();
    }
    console.log('watchAllMySellContracts', myBusinessID);
    const sellContractsRef = collection(this.firestore, 'sellContracts');
    const q = query(
      sellContractsRef,
      or(
        where('buyerBusinessID', '==', myBusinessID),
        where('sellerBusinessID', '==', myBusinessID)
      )
    );
    const sellContractsSubscription = onSnapshot(q, (sellContracts) => {
      const data = sellContracts.docs.map((doc) => {
        const contractData = Object.assign({}, doc.data(), {
          contractDocumentID: doc.id,
          deliveryDate: doc.data()['deliveryDate'] && typeof doc.data()['deliveryDate'].toDate === 'function'
            ? doc.data()['deliveryDate'].toDate()
            : null,
        });
        // Only log X43BHG-8SAWI3 to reduce noise
        if (contractData['contractID'] === 'X43BHG-8SAWI3') {
          console.log(`CONTRACTS_DEBUG SellContract FOUND: ${contractData['contractID']} Status:${contractData['status']} SellerBID:${contractData['sellerBusinessID']}`);
        }
        return contractData;
      });
      allSellContracts.next(data);
      this.sellContracts = data as SellContract[];
      this.sortSellContracts();
      // sort first before doing the tally/ies
      // get all the transactionLogs then perform the tally
      // this will also call tallyMyContracts
      this.tallyMyContractsLevel1();
      this.watchContractTransactionLogs();
    });
    this.sellContractsWatcher = sellContractsSubscription;
    this.sellContractsObserver = allSellContracts.asObservable();
    return this.sellContractsObserver;
  }
  unwatchAllMySellContracts() {
    if (this.sellContractsWatcher !== undefined && typeof this.sellContractsWatcher === 'function') {
      this.sellContractsWatcher();
    }
    this.sellContracts = [];
  }

  unwatchTransactionLogs() {
    if (this.transactionLogsWatcher !== undefined && typeof this.transactionLogsWatcher === 'function') {
      console.log('ContractsService: Unwatching transaction logs');
      this.transactionLogsWatcher();
      this.transactionLogsWatcher = undefined;
      this.isWatchingTransactionLogs = false;
    }
  }

  async watchMyBuyContracts(buyerBusinessID: string): Promise<Observable<any[]>> {
    const allBuyContracts = new Subject<any[]>();
    const buyContractsRef = collection(this.firestore, 'buyContracts');
    const q = query(buyContractsRef, where('buyerBusinessID', '==', buyerBusinessID));
    
    const buyContractsSubscription = onSnapshot(q, (buyContracts) => {
      const data = buyContracts.docs.map((doc) =>
        Object.assign({}, doc.data(), { contractDocumentID: doc.id })
      );
      allBuyContracts.next(data);
    });
    
    return allBuyContracts.asObservable();
  }

  async watchMySellContracts(sellerBusinessID: string): Promise<Observable<any[]>> {
    const allSellContracts = new Subject<any[]>();
    const sellContractsRef = collection(this.firestore, 'sellContracts');
    const q = query(sellContractsRef, where('sellerBusinessID', '==', sellerBusinessID));
    
    const sellContractsSubscription = onSnapshot(q, (sellContracts) => {
      const data = sellContracts.docs.map((doc) =>
        Object.assign({}, doc.data(), { contractDocumentID: doc.id })
      );
      allSellContracts.next(data);
    });
    
    return allSellContracts.asObservable();
  }

  sortBuyContracts() {
    this.buyContracts.forEach((contract: BuyContract) => {
      if (
        contract.signedBuyer !== undefined &&
        contract.signedSeller === undefined
      ) {
        // do nothing since it is waiting for the seller to sign
      } else if (
        contract.signedBuyer !== undefined &&
        contract.signedSeller !== undefined
      ) {
        switch (contract.status) {
          case 'new':
            break;
          case 'pending':
            this.contractAdd('pending', contract, 'buy');
            break;
          case 'fulfill':
            this.contractAdd('fulfill', contract, 'buy');
            break;
          case 'invoice':
            this.contractAdd('invoice', contract, 'buy');
            break;
          case 'dispatch':
            this.contractAdd('dispatch', contract, 'buy');
            break;
          case 'closed':
            this.contractAdd('closed', contract, 'buy');
            break;
          default:
            break;
        }
      }
    });
    this.toggleShowContracts();
    this.abstractsSubject.next(this.abstracts);
  }

  sortSellContracts() {
    this.sellContracts.forEach((contract: SellContract) => {
      if (
        contract.signedSeller !== undefined &&
        contract.signedBuyer === undefined
      ) {
        // do nothing since it is not yet signed by the buyer
      } else if (
        contract.signedBuyer !== undefined &&
        contract.signedSeller !== undefined
      ) {
        switch (contract.status) {
          case 'new':
            break;
          case 'pending':
            this.contractAdd('pending', contract, 'sell');
            break;
          case 'fulfill':
            this.contractAdd('fulfill', contract, 'sell');
            break;
          case 'invoice':
            this.contractAdd('invoice', contract, 'sell');
            break;
          case 'dispatch':
            this.contractAdd('dispatch', contract, 'sell');
            break;
          case 'closed':
            this.contractAdd('closed', contract, 'sell');
            break;
          default:
            break;
        }
      }
    });
    this.toggleShowContracts();
    this.abstractsSubject.next(this.abstracts);
  }

  watchContractTransactionLogs() {
    // Clean up existing watcher first
    this.unwatchTransactionLogs();

    const contractIDs = this.abstracts.map((contract) => {
      return contract['contractID'];
    });

    if (contractIDs.length > 0) {
      console.log(`ContractsService: Starting real-time transaction logs watcher for ${contractIDs.length} contracts`);
      
      const logRef = collection(this.firestore, 'transactionLogs');
      const q = query(logRef, where('contractID', 'in', contractIDs));
      
      // Use onSnapshot for real-time updates instead of getDocs
      const transactionLogsSubscription = onSnapshot(q, (querySnapshot) => {
        try {
          console.log(`[REALTIME-LOGS] Received ${querySnapshot.docs.length} transaction logs`);
          
          // Reset transaction logs for fresh data
          this.transactionLogs = {};
          
          // Process all transaction logs
          querySnapshot.docs.forEach((doc) => {
            const logData = doc.data();
            const contractID = logData['contractID'];
            
            if (this.transactionLogs[contractID] === undefined) {
              this.transactionLogs[contractID] = [];
            }
            
            this.transactionLogs[contractID].push(
              Object.assign({}, logData, { id: doc.id })
            );
          });
          
          console.log('[REALTIME-LOGS] Transaction logs updated:', Object.keys(this.transactionLogs).length, 'contracts');
          
          // Broadcast transaction logs update
          this.transactionLogsSubject.next(this.transactionLogs);
          
          // Trigger contract tally recalculation with fresh logs
          this.tallyMyContractsLevel2();
          
        } catch (error) {
          console.error('ContractsService: Error processing transaction logs:', error);
        }
      }, (error) => {
        console.error('ContractsService: Transaction logs watcher error:', error);
      });
      
      this.transactionLogsWatcher = transactionLogsSubscription;
      this.isWatchingTransactionLogs = true;
      
    } else {
      console.log('ContractsService: No contracts to watch for transaction logs');
      // Still trigger tally for consistency
      this.tallyMyContractsLevel2();
    }
  }

  insertLog(log: any) {
    console.log('insertLog', log);
    // get the transactionLog array for contractID
    if (this.transactionLogs[log.contractID] === undefined) {
      this.transactionLogs[log.contractID] = [];
    }
    let bin = this.transactionLogs[log.contractID];
    // find the log.id in the array
    let foundIndex = bin.findIndex((a: any) => {
      return a.id == log.id;
    });
    if (foundIndex >= 0) {
      // if it is there, then replace in place
      //bin[foundIndex] = log;
    } else {
      // else push it into the list
      bin.push(log);
    }
  }

  findInLogs(contractID: string, code: string): any {
    // Enhanced debugging for delivery-related logs
    const isDeliveryLog = code.includes('delivery');
    
    // Only log for X43BHG-8SAWI3 and delivery-related searches to reduce noise
    if (contractID === 'X43BHG-8SAWI3' || isDeliveryLog) {
      console.log(`LOGS_DEBUG findInLogs: ${contractID} searching for '${code}'`);
    }
    
    // get the transactionLog array for contractID
    if (this.transactionLogs[contractID] === undefined) {
      if (contractID === 'X43BHG-8SAWI3' || isDeliveryLog) {
        console.log(`LOGS_DEBUG ${contractID} No transaction logs found`);
      }
      if (isDeliveryLog) {
        console.log(`[DELIVERY-HISTORY-DEBUG] findInLogs: No transaction logs found for contract ${contractID}`);
      }
      return false;
    } else {
      let bin = this.transactionLogs[contractID];
      
      if (contractID === 'X43BHG-8SAWI3' || isDeliveryLog) {
        console.log(`LOGS_DEBUG ${contractID} Available logs:`, bin.map((log: any) => log.logCode));
      }
      
      if (isDeliveryLog) {
        console.log(`[DELIVERY-HISTORY-DEBUG] findInLogs: Searching for '${code}' in contract ${contractID}`);
        console.log(`[DELIVERY-HISTORY-DEBUG] Available logs for ${contractID}:`, bin.map((log: any) => ({
          logCode: log.logCode,
          referenceNum: log.referenceNum,
          createdAt: log.createdAt,
          userID: log.userID
        })));
      }
      
      // find the log.id in the array
      let foundIndex = bin.findIndex((a: any) => {
        return a.logCode == code;
      });

      if (foundIndex >= 0) {
        if (contractID === 'X43BHG-8SAWI3' || isDeliveryLog) {
          console.log(`LOGS_DEBUG ${contractID} Found '${code}' at index ${foundIndex}`);
        }
        if (isDeliveryLog) {
          console.log(`[DELIVERY-HISTORY-DEBUG] findInLogs: Found '${code}' at index ${foundIndex}:`, bin[foundIndex]);
        }
        return bin[foundIndex];
      } else {
        if (contractID === 'X43BHG-8SAWI3' || isDeliveryLog) {
          console.log(`LOGS_DEBUG ${contractID} '${code}' not found in logs`);
        }
        if (isDeliveryLog) {
          console.log(`[DELIVERY-HISTORY-DEBUG] findInLogs: '${code}' not found in logs for ${contractID}`);
        }
        return false;
      }
    }
  }

  clearFromLogs(contractID: string, logCode: string) {
    console.log('clearFromLogs', contractID, logCode);
    let bin = this.transactionLogs[contractID];
    // find the log.id in the array
    // console.log('findInLogs', contractID, code, bin);
    let foundIndex = bin.findIndex((a: any) => {
      return a.logCode == logCode;
    });

    if (foundIndex >= 0) {
      console.log(
        'clearFromLogs : found',
        bin[foundIndex],
        logCode,
        contractID,
        foundIndex
      );
      // remove the log from the transactionLogs
      this.transactionLogs[contractID].splice(foundIndex, 1); // remove the log from the transactionLogs
      console.log('clearFromLogs', this.transactionLogs[contractID]);
    }
  }

  iAmBuyer(contract: any, businessID: string) {
    return contract.buyerBusinessID == businessID;
  }

  iAmSeller(contract: any, businessID: string) {
    return contract.sellerBusinessID == businessID;
  }

  /**
   * LAK-333: Simplified tally calculation using backend API
   * Replaces the complex frontend logic with backend call
   */
  tallyMyContractsLevel1() {
    console.log('tallyMyContractsLevel1: myBusinessID =', this.myBusinessID);
    // LAK-333: Only call backend if we have a business ID
    if (this.myBusinessID) {
      this.getContractTallyFromBackend();
    } else {
      console.warn('tallyMyContractsLevel1: Skipping backend call - no business ID yet');
    }
  }
  /**
   * LAK-333: Simplified Level2 tally calculation using backend API
   * Replaces the complex transaction log analysis with backend call
   */
  tallyMyContractsLevel2() {
    console.log('tallyMyContractsLevel2: myBusinessID =', this.myBusinessID);
    // LAK-333: Only call backend if we have a business ID
    if (this.myBusinessID) {
      this.getContractTallyFromBackend();
    } else {
      console.warn('tallyMyContractsLevel2: Skipping backend call - no business ID yet');
    }
  }

  subscribeTallyOfContracts(onNext: any) {
    return this.tallyOfContractsSubject.subscribe(onNext);
  }

  /**
   * LAK-333: Get contract tally from backend API
   * Replaces frontend calculation with backend service
   */
  private getContractTallyFromBackend() {
    if (!this.myBusinessID) {
      console.warn('ContractsService: No business ID available for tally calculation');
      return;
    }

    const url = `${this.apiUrl}/contracts/dashboard/${this.myBusinessID}/tally`;
    console.log('ContractsService: Making tally request to:', url);
    
    this.http.get<any>(url).subscribe({
      next: (response) => {
        console.log('ContractsService: Raw tally response from backend:', response);
        if (response.success && response.tally) {
          console.log('ContractsService: Processing tally data:', response.tally);
          console.log('ContractsService: Before update - tallyOfContracts:', this.tallyOfContracts);
          Object.assign(this.tallyOfContracts, response.tally);
          console.log('ContractsService: After update - tallyOfContracts:', this.tallyOfContracts);
          this.tallyOfContractsSubject.next(this.tallyOfContracts);
          console.log('ContractsService: Emitted updated tally to subscribers');

          // Update contract abstracts with centralized calculations for consistency
          this.updateContractAbstractsWithCentralizedCalculations().catch(error => {
            console.warn('ContractsService: Failed to update centralized calculations:', error);
          });
        } else {
          console.error('ContractsService: Invalid tally response from backend:', response);
        }
      },
      error: (error) => {
        console.error('ContractsService: Error fetching tally from backend:', error);
        
        // For sandbox environment, provide mock tally data to show tabs
        if (this.apiUrl.includes('lakal-trading-app')) {
          console.log('ContractsService: Using mock tally data for sandbox environment');
          const mockTally = {
            numBuyContracts: 2,
            numSellContracts: 1,
            numPending: 1,
            numToBook: 1, // Show booking tab
            numBooked: 0,
            numToFund: 1, // Show funding tab
            numFunded: 0,
            numFulfillment: 1, // Show fulfillment tab
            numInbound: 0,
            numReceiving: 0,
            numInvoicing: 1, // Show invoicing tab
            numToTransfer: 0,
            numConfirmedTransfer: 0,
            numTransferred: 0,
            numToIssueInvoice: 0,
            numInvoiceIssued: 0,
            numPacked: 0,
            numInTransit: 0,
            numDispatch: 1, // Show dispatch tab
            numReleased: 0,
            numClosed: 0,
            numToDisburse: 1, // Show disbursement tab
            numDisbursed: 0
          };
          
          Object.assign(this.tallyOfContracts, mockTally);
          this.tallyOfContractsSubject.next(this.tallyOfContracts);
          console.log('ContractsService: Applied sandbox mock tally data:', this.tallyOfContracts);
        }
        
        // Continue with existing tally in case of API failure
      }
    });
  }

  /**
   * LAK-333: Get complete dashboard data from backend
   * New method to fetch all contract dashboard data
   */
  getDashboardData(): Observable<any> {
    if (!this.myBusinessID) {
      throw new Error('No business ID available for dashboard data');
    }

    const url = `${this.apiUrl}/contracts/dashboard/${this.myBusinessID}`;
    return this.http.get<any>(url);
  }

  /**
   * LAK-333: Get specific tab data from backend
   * New method to fetch contracts for a specific tab
   */
  getContractTabData(tab: string): Observable<any> {
    if (!this.myBusinessID) {
      throw new Error('No business ID available for tab data');
    }

    const url = `${this.apiUrl}/contracts/dashboard/${this.myBusinessID}/${tab}`;
    return this.http.get<any>(url);
  }

  contractAdd(
    state: string,
    contract: BuyContract | SellContract,
    contractType: string = 'buy'
  ) {
    // get the list

    let bin = this.allContracts[state];
    // check if it is there
    let foundIndex = bin.findIndex((a: any) => {
      return a.contractID == contract.contractID;
    });
    // if it is there, then replace in place
    // console.log('contractAdd', foundIndex);
    if (foundIndex >= 0) {
      bin[foundIndex] = contract;
    } else {
      // else push it into the list
      bin.push(contract);
      Object.keys(this.allContracts).forEach((key) => {
        if (key != state) this.contractDelete(key, contract.contractID);
      });
    }
    //console.log('contractAdd', state, this.allContracts);
    this.contractAddAbstract(contract, contractType);
  }

  contractAddAbstract(
    contract: BuyContract | SellContract,
    contractType: string = 'buy'
  ) {
    // Business role-based filtering to prevent dual-contract overwrites
    const userBusinessID = this.myBusinessID;
    const isUserBuyerBusiness = contract.buyerBusinessID === userBusinessID;
    const isUserSellerBusiness = contract.sellerBusinessID === userBusinessID;

    // Only add contract if it matches user's business role
    if ((contractType === 'buy' && !isUserBuyerBusiness) ||
        (contractType === 'sell' && !isUserSellerBusiness)) {
      // Skip this contract - user is not the correct business party
      console.log(`🔍 [CONTRACTS-DEBUG] Skipping ${contractType} contract ${contract.contractID} - user business ${userBusinessID} not matching`);
      return;
    }

    let abstract: IItem = {
      contractID: contract.contractID,
      code: contract.commodity.code,
      qty: contract.contractQuantity,
      price: contract.contractPrice,
      qtyFormatted: contract.contractQuantity.toLocaleString('en-US'),
      priceFormatted: contract.contractPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: 'PHP',
      }),
      status: contract.status,
      deliveryDate: contract.deliveryDate,
      deliveryDateFormatted: contract.deliveryDate instanceof Date && !isNaN(contract.deliveryDate.getTime())
        ? contract.deliveryDate.toLocaleDateString()
        : '', // Provide a default empty string or handle as needed
      // Add timestamp for proper date sorting
      deliveryDateTimestamp: contract.deliveryDate instanceof Date && !isNaN(contract.deliveryDate.getTime())
        ? contract.deliveryDate.getTime()
        : 0,
      stockpoint: contract.stockpoint.name,
      stockpointID: contract.stockpoint.stockpointID,
      commodity: contract.commodity.name,
      uom: contract.commodity.uom,
      buyerBusinessID: contract.buyerBusinessID,
      sellerBusinessID: contract.sellerBusinessID,
      buyerAmountDue: this.calculateAmountDue(contract, 'buyer'),
      sellerAmountDue: this.calculateAmountDue(contract, 'seller'),
      contractType: contractType,
      contractDocID: contract.contractDocumentID,
      // Include partial payment tracking fields (buyer-only)
      paidAmount: (contract as any).paidAmount || undefined,
      remainingAmount: (contract as any).remainingAmount || undefined,
      partialPayments: (contract as any).partialPayments || undefined,
    };

    // Debug log for BFORXO-6SPX84
    if (contract.contractID === 'BFORXO-6SPX84') {
      console.log('🔍 [CONTRACTS-DEBUG] Building abstract for BFORXO-6SPX84:', {
        contractID: contract.contractID,
        buyerAmountDue: abstract['buyerAmountDue'],
        remainingAmount: (contract as any).remainingAmount,
        paidAmount: (contract as any).paidAmount,
        partialPayments: (contract as any).partialPayments
      });
    }
    // find the index of the contract in abstracts using contractID
    let foundIndex = this.abstracts.findIndex((a: any) => {
      return a.contractID == contract.contractID;
    });
    // if it is there, then replace in place
    if (foundIndex >= 0) {
      this.abstracts[foundIndex] = abstract;
    } else {
      // else push it into the list
      this.abstracts.push(abstract);
    }
    // console.log('contractAddAbstract', this.abstracts);
  }

  /**
   * Update contract abstracts with centralized calculation API amounts
   * This provides consistent amounts across all components
   */
  async updateContractAbstractsWithCentralizedCalculations(): Promise<void> {
    try {
      console.log('[CONTRACTS-API] Updating contract abstracts with centralized calculations...');

      const updatePromises = this.abstracts.map(async (abstract: any) => {
        try {
          // Determine business role for this contract
          const isMyBuyContract = abstract.buyerBusinessID === this.currentBusinessID;
          const isMySellContract = abstract.sellerBusinessID === this.currentBusinessID;

          if (isMyBuyContract) {
            // I'm the buyer, get buyer amount due
            const buyerAmount = await this.calculateAmountDueFromAPI(abstract.contractID, 'buyer');
            abstract.buyerAmountDue = buyerAmount;
          }

          if (isMySellContract) {
            // I'm the seller, get seller amount due
            const sellerAmount = await this.calculateAmountDueFromAPI(abstract.contractID, 'seller');
            abstract.sellerAmountDue = sellerAmount;
          }

          console.log(`[CONTRACTS-API] Updated amounts for ${abstract.contractID}:`, {
            buyerAmountDue: abstract.buyerAmountDue,
            sellerAmountDue: abstract.sellerAmountDue
          });

        } catch (error) {
          console.warn(`[CONTRACTS-API] Failed to update amounts for ${abstract.contractID}, keeping legacy values:`, error);
        }
      });

      await Promise.all(updatePromises);

      // Emit updated abstracts
      this.abstractsSubject.next(this.abstracts);
      console.log('[CONTRACTS-API] Contract abstracts updated with centralized calculations');

    } catch (error) {
      console.error('[CONTRACTS-API] Error updating contract abstracts with centralized calculations:', error);
    }
  }

  /**
   * Call centralized calculation API for consistent contract amounts
   */
  async calculateAmountDueFromAPI(contractID: string, role: 'buyer' | 'seller'): Promise<number> {
    try {
      const endpoint = role === 'buyer' ? 'buyer-amount-due' : 'seller-amount-due';
      const response = await this.http.get<any>(`${this.apiUrl}/contracts/${contractID}/calculate/${endpoint}`).toPromise();

      if (response && response.success && response.data) {
        return response.data.totalAmountDue;
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.error(`[CONTRACTS-API] Error calling centralized calculation for ${contractID} as ${role}:`, error);
      throw error;
    }
  }

  /**
   * Calculate service fees for a contract using centralized API
   */
  async calculateServiceFeesFromAPI(contractID: string, businessRole: 'buyer' | 'seller'): Promise<any> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/contracts/${contractID}/calculate/service-fees?businessRole=${businessRole}`).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Invalid service fees response');
      }
    } catch (error) {
      console.error(`[CONTRACTS-API] Error calculating service fees for ${contractID} as ${businessRole}:`, error);
      throw error;
    }
  }

  /**
   * Calculate partial payment amounts using centralized API
   */
  async calculatePartialPaymentFromAPI(contractID: string, paymentAmount: number, businessRole: 'buyer' | 'seller' = 'buyer'): Promise<any> {
    try {
      const response = await this.http.post<any>(`${this.apiUrl}/contracts/${contractID}/calculate/partial-payment`, {
        paymentAmount,
        businessRole
      }).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Invalid partial payment response');
      }
    } catch (error) {
      console.error(`[CONTRACTS-API] Error calculating partial payment for ${contractID}:`, error);
      throw error;
    }
  }

  /**
   * Validate calculation consistency for a contract using centralized API
   */
  async validateCalculationConsistencyFromAPI(contractID: string): Promise<any> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/contracts/${contractID}/calculate/validate`).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Invalid validation response');
      }
    } catch (error) {
      console.error(`[CONTRACTS-API] Error validating calculations for ${contractID}:`, error);
      throw error;
    }
  }

  /**
   * Get comprehensive calculation breakdown for a contract
   */
  async getCalculationBreakdownFromAPI(contractID: string, businessRole: 'buyer' | 'seller'): Promise<any> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/contracts/${contractID}/calculate/breakdown?businessRole=${businessRole}`).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Invalid breakdown response');
      }
    } catch (error) {
      console.error(`[CONTRACTS-API] Error getting calculation breakdown for ${contractID}:`, error);
      throw error;
    }
  }

  /**
   * Calculate amount due for a contract (legacy method with API fallback)
   * @deprecated Consider using calculateAmountDueFromAPI for consistency with backend
   */
  calculateAmountDue(contract: BuyContract | SellContract, role: string) {
    let amountDue = 0;
    if (role == 'buyer') {
      // add the price of the commodity
      amountDue = contract.contractQuantity * contract.contractPrice;
      // add the buyerCharges
      // console.log(
      //   'adding buyer charges',
      //   contract.contractID,
      //   contract.buyerCharges
      // );
      if (Array.isArray(contract.buyerCharges)) {
        amountDue += contract.buyerCharges.reduce(
          (a, b) => a + b.amount + b.tax,
          0
        );
      }
      // add the lakalCharges
      if (Array.isArray(contract.lakalCharges)) {
        amountDue += contract.lakalCharges.reduce(
          (a, b) => a + b.amount + b.tax,
          0
        );
      }

      // For contracts with partial payments, return the remaining amount instead of full amount
      const contractWithPayment = contract as any;

      // Debug log for BFORXO-6SPX84
      if (contract.contractID === 'BFORXO-6SPX84') {
        console.log('🔍 [AMOUNT-DUE-DEBUG] calculateAmountDue called for BFORXO-6SPX84:', {
          contractID: contract.contractID,
          role: role,
          fullAmount: amountDue,
          remainingAmount: contractWithPayment.remainingAmount,
          paidAmount: contractWithPayment.paidAmount,
          partialPayments: contractWithPayment.partialPayments,
          hasRemainingAmount: contractWithPayment.remainingAmount !== undefined,
          hasPaidAmount: contractWithPayment.paidAmount !== undefined,
          willUsePartialPayment: contractWithPayment.remainingAmount !== undefined && contractWithPayment.paidAmount !== undefined
        });
      }

      if (contractWithPayment.remainingAmount !== undefined && contractWithPayment.paidAmount !== undefined) {
        // Debug log for BFORXO-6SPX84
        if (contract.contractID === 'BFORXO-6SPX84') {
          console.log('🔍 [AMOUNT-DUE-DEBUG] Using partial payment for BFORXO-6SPX84:', {
            returning: contractWithPayment.remainingAmount
          });
        }
        return Math.round(contractWithPayment.remainingAmount * 100) / 100;
      }
    } else {
      // add seller charges to amount due
      if (Array.isArray(contract.sellerCharges)) {
        amountDue += contract.sellerCharges.reduce(
          (a, b) => a + b.amount + b.tax,
          0
        );
      }
    }
    // do not add taxumo payment processing fee as it is per payment order not per contract
    // amountDue += 30;
    // return amount due rounded up to two decimal places
    return Math.round(amountDue * 100) / 100;
  }

  toggleShowContracts() {
    this.showContracts = false;
    console.log('toggleShowContracts before', this.showContracts);
    setTimeout(() => {
      this.showContracts = true;
      console.log('toggleShowContracts after', this.showContracts);
    }, 200);
  }

  contractDelete(state: string, contractID: string) {
    // get the list
    let bin = this.allContracts[state];
    // check if it is there
    let foundIndex = bin.findIndex((a: any) => {
      return a.contractID == contractID;
    });
    // if it is there, then remove it
    if (foundIndex >= 0) {
      bin.splice(foundIndex, 1);
    } else {
      // ok, did not find it in this que
    }
  }

  updateContractStatus(
    contractDocID: string,
    contractType: string,
    newStatus: string
  ): Promise<any> {
    console.log('updateContractStatus: DUAL CONTRACT update', contractDocID, contractType, newStatus);
    return new Promise(async (resolve, reject) => {
      try {
        // Get the contract document to find the contractID
        let path = '';
        if (contractType == 'sell') {
          path = 'sellContracts/' + contractDocID;
        } else {
          path = 'buyContracts/' + contractDocID;
        }
        
        const contractRef = doc(this.firestore, path);
        const contractSnapshot = await getDoc(contractRef);
        
        if (!contractSnapshot.exists()) {
          throw new Error(`Contract document not found at ${path}`);
        }
        
        const contractData = contractSnapshot.data();
        const contractID = contractData['contractID'];
        
        if (!contractID) {
          throw new Error(`Contract ID not found in document ${contractDocID}`);
        }
        
        console.log('updateContractStatus: Found contractID:', contractID, 'updating both contracts');
        
        // Use dual contract sync service to update both contracts
        const updateResult = await this.dualContractSync.updateBothContractsStatus(contractID, newStatus);
        
        if (updateResult.success) {
          console.log('updateContractStatus: successfully updated both contracts');
          resolve({ success: true, signed: updateResult });
        } else {
          throw new Error(updateResult.error);
        }
      } catch (err) {
        console.log(
          'updateContractStatus: error while updating dual contracts',
          contractDocID,
          err
        );
        reject({
          success: false,
          msg: 'error while updating dual contracts',
          err: err,
        });
      }
    });
  }

  writeTransactionLog(
    contractID: string,
    logCode: string,
    logMessage: string,
    userID: string,
    referenceNum: string = ''
  ): Promise<any> {
    console.log(
      'writeTransactionLog',
      contractID,
      logCode,
      logMessage,
      userID,
      referenceNum
    );
    return new Promise((resolve, reject) => {
      const logRef = collection(this.firestore, 'transactionLogs');
      const logData = {
        contractID: contractID,
        logCode: logCode,
        logMessage: logMessage,
        logDate: new Date(),
        referenceNum: referenceNum,
      };
      addDoc(logRef, logData)
        .then((result) => {
          console.log('writeTransactionLog: added log', result);
          this.watchContractTransactionLogs();
          resolve({ success: true, result: result });
        })
        .catch((err) => {
          console.log('writeTransactionLog: error adding log', err);
          reject({ success: false, err: err });
        });
    });
  }

  deleteTransactionLogByReference(
    refID: string,
    logCode: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      // find transactionLogs with logCode == logCode and referenceNum == refID
      // then prefix the logCode with 'delete-'
      const logRef = collection(this.firestore, 'transactionLogs');
      const q = query(
        logRef,
        where('logCode', '==', logCode),
        where('referenceNum', '==', refID)
      );
      getDocs(q).then((logs) => {
        logs.forEach((log) => {
          console.log('deleteTransactionLogByReference', log.id, log.data());
          // update the logCode to 'delete-' + logCode
          const logData = {
            logCode: 'delete-' + logCode,
          };
          const docRef = doc(this.firestore, 'transactionLogs', log.id);
          updateDoc(docRef, logData).then(
            (result) => {
              console.log(
                'deleteTransactionLogByReference: updated log',
                result
              );
              resolve({ success: true, result: result });
            },
            (err) => {
              console.log(
                'deleteTransactionLogByReference: error updating log',
                err
              );
              reject({ success: false, err: err });
            }
          );
        });
      });
    });
  }

  async bookDelivery(contractInfo: any): Promise<any> {
    console.log('bookDelivery', contractInfo);
    
    try {
      // Get contract ID from contractInfo
      const contractID = contractInfo.contractID;
      if (!contractID) {
        throw new Error('Contract ID is required for delivery booking');
      }

      // Call the contract state machine API to perform delivery-booked action
      const response = await this.http.post(`${this.apiUrl}/contracts/${contractID}/actions`, {
        actionCode: 'delivery-booked',
        actor: 'seller',
        actorUserId: this.myBusinessID,
        message: 'Delivery booked via trade app'
      }).toPromise() as any;

      console.log('bookDelivery API response:', response);
      
      if (response && response.success) {
        // If contract state changed, refresh contracts
        if (response.stateChanged) {
          console.log(`Contract ${contractID} state changed to: ${response.newState}`);
          // The contract abstracts will be updated via the existing subscription
        }
        
        return { 
          success: true, 
          contractID: contractID,
          newState: response.newState,
          message: 'Delivery booking completed successfully'
        };
      } else {
        throw new Error('API call failed');
      }
    } catch (error: any) {
      console.error('Error booking delivery:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to book delivery',
        message: `Failed to book delivery: ${error.message || 'Unknown error'}`
      };
    }
  }

  requestPreFund(contractInfo: any): Promise<any> {
    console.log('requestPreFund', contractInfo);
    // if the contract was also booked for delivery, then the status is 'fulfill' but catch this through functions
    return new Promise((resolve) => {
      resolve({ success: true });
    });
  }

  performReceiving(contractInfo: any): Promise<any> {
    console.log('performReceiving', contractInfo);
    return this.updateContractStatus(
      contractInfo.contractDocID,
      contractInfo.contractType,
      'invoicing'
    );
  }

  transferPayment(contractInfo: any): Promise<any> {
    console.log('transferPayment', contractInfo);
    return this.updateContractStatus(
      contractInfo.contractDocID,
      contractInfo.contractType,
      'dispatch'
    );
  }

  performDispatch(contractInfo: any): Promise<any> {
    console.log('performShipping', contractInfo);
    return this.updateContractStatus(
      contractInfo.contractDocID,
      contractInfo.contractType,
      'closed'
    );
  }

  ngOnDestroy() {
    console.log('ContractsService: Cleaning up all watchers');
    this.unwatchAllMyBuyContracts();
    this.unwatchAllMySellContracts();
    this.unwatchTransactionLogs();
  }
}
